import React from "react"
import { motion } from "framer-motion"

import {
    AI_MESSAGE_ENTER_VARIANTS,
    USER_MESSAGE_ENTER_VARIANTS,
} from "~components/shared/motion/motion-variants"
import { componentStylesCss } from "~utils/helpers/style-helpers"

// Semantic alignment values for chat bubbles. AI messages render on the
// leading edge, user messages on the trailing edge. Prefer these over raw
// "start" / "end" strings at call sites.
export const MESSAGE_ALIGN = {
    AI: "start",
    USER: "end",
} as const
type MessageAlign = (typeof MESSAGE_ALIGN)[keyof typeof MESSAGE_ALIGN]

const STYLES = componentStylesCss({
    aiMessage: {
        display: "flex",
        flexDirection: "column",
        alignSelf: "stretch",
        // Span the full available width so children that should fill the row
        // (e.g. step separators) are full width regardless of whether the AI
        // message currently contains a text response or only tool-call chips.
        width: "100%",
        maxWidth: "100%",
    },
    userMessage: {
        display: "flex",
        flexDirection: "column",
        alignSelf: "flex-end",
        maxWidth: "100%",
    },
})

interface Props {
    readonly children: React.ReactNode
    readonly align: MessageAlign
}

export default function AnimatedMessage({ children, align }: Props) {
    const isAi = align === MESSAGE_ALIGN.AI
    const variants = isAi
        ? AI_MESSAGE_ENTER_VARIANTS
        : USER_MESSAGE_ENTER_VARIANTS

    return (
        <motion.div
            layout="position"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={isAi ? STYLES.aiMessage : STYLES.userMessage}
        >
            {children}
        </motion.div>
    )
}
